export type UserType = 'internal' | 'external';

export enum AppRole {
  // Regional Supervisors
  SUPERVISOR_NAM = 'Supervisor NAM',
  SUPERVISOR_EMEA = 'Supervisor EMEA',
  SUPERVISOR_APAC = 'Supervisor APAC',

  // Default Sponsors (Regional)
  DEFAULT_SPONSOR_NAM = 'Default Sponsor NAM',
  DEFAULT_SPONSOR_EMEA = 'Default Sponsor EMEA',
  DEFAULT_SPONSOR_APAC = 'Default Sponsor APAC',

  // Standard Roles
  REQUESTOR = 'Requestor',
  EXTERNAL_REQUESTOR = 'External Requestor',
  EXTERNAL_REQUESTOR_LVO = 'External Requestor LVO',
  VERTIV_CONTRIBUTOR = 'Vertiv Contributor',
  PARTNER_CONTRIBUTOR = 'Partner Contributor',
  PARTNER_CONTRIBUTOR_LVO = 'Partner Contributor LVO',
  CONFIG_ENGINEER = 'Config Engineer',
  VERTIV_SPONSOR = 'Vertiv Sponsor',
  IT_ADMINISTRATOR = 'IT Administrator',
  BUSINESS_OWNER = 'Business Owner',
  ROLE_MANAGER = 'Role Manager',

  // Impersonation Roles
  IMPERSONATOR_FULL_ACCESS = 'Impersonator Full Access',
  IMPERSONATOR_READ_ONLY = 'Impersonator Read Only',
}

export enum Permission {
  CREATE_REQUEST = 'CREATE_REQUEST',
  VIEW_REQUEST = 'VIEW_REQUEST',
  EDIT_REQUEST = 'EDIT_REQUEST',
  CLOSE_REQUEST = 'CLOSE_REQUEST',
  MANAGE_SPONSORS = 'MANAGE_SPONSORS',
  MANAGE_CONTRIBUTORS = 'MANAGE_CONTRIBUTORS',
  MANAGE_ROLES = 'MANAGE_ROLES',
}

export interface User {
  id: string;
  /** Database `users.user_id`. Never use `id` / Okta `sub` as a Bind pending key. */
  userId?: number;
  firstName?: string;
  lastName?: string;
  name: string;
  email: string;
  phone: string;
  userType: UserType;
  adGroups: string[];
  roles: AppRole[];
}

export type AuthStatusResult =
  | { kind: 'error' }
  | { kind: 'anonymous' }
  | { kind: 'user'; user: User };

export interface AuthState {
  isAuthenticated: boolean;
  user: User | null;
  isLoading: boolean;
  error: string | null;
}

export interface AuthContextType extends AuthState {
  login: (userId?: string) => Promise<void>;
  logout: () => Promise<void>;
  clearError: () => void;
  validateSession: () => Promise<boolean>;
  refreshAuth: () => Promise<void>;
  // Bumped whenever roleService.hydrateRolePermissions()/resetRolePermissions() runs. roleService
  // is a plain singleton with no subscription mechanism of its own — consumers that memoize a
  // roleService permission check (e.g. useFormPermissions' rolePermissions) must include this in
  // their dependency array, or the memo never recomputes after login-time hydration completes.
  permissionsHydrationVersion: number;
}

// Re-export UserProfile from dedicated user types file for backward compatibility
// New code should import directly from '../types/user'
export type { UserProfile } from './user';

// Authentication service interface
export interface IAuthService {
  login(userId?: string): Promise<User>;
  logout(): Promise<void>;
  getCurrentUser(): Promise<User | null>;
  getAuthStatus(): Promise<AuthStatusResult>;
  getAllUsers?(): Promise<User[]>; // Optional - only available in mock service
  refreshToken?(): Promise<string>;
  clearUserCache?(): void;
  // Impersonation token management (native only)
  stashTokensForImpersonation?(): Promise<void>;
  restoreStashedTokens?(): Promise<boolean>;
  clearStashedTokens?(): Promise<void>;
  setToken?(token: string): Promise<void>;
}
